import { useRtDetails } from '../queries';
import { MonetaryAmountDataPointValue, RealEstateEquityLineItem } from '../../generated/graphql';
import { FinancialSummaryTableRow } from '../../UI';
import { displayRelativeDateFromNow } from '../../util';
import { useTranslation } from 'react-i18next';
import { displayAddress } from '../../RealEstateProperty';

export const useRealEstateEquityDetails = (householdID: string) => {
  const { rtDetails } = useRtDetails({
    householdID,
    includeRealEstate: true,
    includeTotalRealEstateEquity: true,
    includeTotalRealEstateDebt: true,
    includeTotalRealEstateValue: true,
  });
  const { t } = useTranslation('pages');

  const getRows = (property: RealEstateEquityLineItem) => {
    let loans: FinancialSummaryTableRow[];
    if (property.loans?.length > 0) {
      loans = property.loans.map((loan) => ({
        amountLabel:
          (loan.balance.latestDataPoint?.dateTime &&
            displayRelativeDateFromNow(loan.balance.latestDataPoint?.dateTime)) ||
          '',
        amount: {
          currencyCode: (loan.balance.latestDataPoint?.data as MonetaryAmountDataPointValue)?.value?.currencyCode,
          value: -1 * ((loan.balance.latestDataPoint?.data as MonetaryAmountDataPointValue)?.value?.value || 0),
        },
        label: loan.name,
      }));
    } else {
      loans = [
        {
          label: t('real-estate-term-element-detail-view-details-table-no-loans'),
          amountLabel: displayRelativeDateFromNow(property.realEstate.value.latestDataPoint?.dateTime),
          amount: {
            currencyCode: (property.realEstate.value.latestDataPoint?.data as MonetaryAmountDataPointValue | undefined)?.value
              ?.currencyCode ?? 'USD',
            value: 0,
          },
        },
      ];
    }
    return [
      ...[
        {
          label: t('real-estate-term-element-detail-view-details-table-estimated-value'),
          amount: (property.realEstate.ownedValue),
          amountLabel: displayRelativeDateFromNow(property.realEstate.value.latestDataPoint?.dateTime),
        },
      ],
      ...loans,
    ];
  };

  const getDescription = (property: RealEstateEquityLineItem) => {
    const address = (property.realEstate.address && displayAddress(property.realEstate.address)) || '';
    if (property.isBusinessOwned) {
      const businessLabel = t('real-estate-term-element-detail-view-business-owned', 'Business Owned Real Estate does not factor into Rt');
      return address ? `${address}\n${businessLabel}` : businessLabel;
    }
    return address;
  };

  return {
    realEstate: [...(rtDetails?.realEstate || [])].sort((a, b) => a.realEstate.name.localeCompare(b.realEstate.name)),
    getRows,
    totalLabel: t('real-estate-term-element-detail-view-details-table-total-equity'),
    getDescription,
    tableHeader: t('real-estate-term-element-detail-view-details-table-header'),
  };
};
